import { rankOf } from '../core/cards';
import { DEFAULT_NOISE_FLOOR_BB } from './ev';
import { gradeAbstention, gradeFromEvLoss } from './bands';
import { attributeDimensions } from './dimensions';
const isPocketPair = (cards) => rankOf(cards[0]) === rankOf(cards[1]);
/**
 * Coarse preflop realization factor (§PF2, mirrors grading/realization.ts's positional split one street
 * earlier): IP realizes more than OOP; a pocket pair gets a set-mining implied-odds bump on top since it
 * can flat cheaply and stack someone when it hits (the classic "flat/defend-EV" driver BUILD_PLAN names).
 */
export const preflopRealizationFactor = (positionClass, pocketPair) => {
    const ip = positionClass === 'IP';
    if (pocketPair)
        return ip ? 1.15 : 1.05;
    return ip ? 1.05 : 0.85;
};
/** EV of a fold-equity raise (open or 3bet), in bb: F·P + (1−F)·[Evc·(P+2b−v) − b] - the same one-street
 *  algebra as grading/ev.ts's evBet, with `b` the raise's bb size passed directly (preflop sizing is
 *  already a bb amount, not a pot fraction). `v` is the amount the field already has committed to `deadPot`
 *  before hero's raise (0 for an open into a fresh pot; the opener's toCallBB for a 3bet) - a 3bet-called
 *  villain only adds `b−v` more, not a fresh `b`, since `v` of their call is already sitting in `deadPot`.
 *  Opus edge-case review (2026-07-14) caught the original version omitting `v`, double-counting the
 *  opener's already-committed chips and over-stating 3bet EV by (1−F)·Evc·v. */
const evRaise = (deadPot, raiseBB, foldProb, equityVsContinue, villainAlreadyInBB = 0) => foldProb * deadPot + (1 - foldProb) * (equityVsContinue * (deadPot + 2 * raiseBB - villainAlreadyInBB) - raiseBB);
/** EV of a flat/call, in bb: min(E·R, 1)·(P + toCall) − toCall - the same evCall algebra as grading/ev.ts,
 *  realization-discounted by the preflop positional + set-mining factor, clamped to a legal probability
 *  (a high-equity IP pocket pair can otherwise push E·R past 1.0 - Opus edge-case review, 2026-07-14). */
const evFlat = (pot, toCall, equity, realization) => Math.min(1, equity * realization) * (pot + toCall) - toCall;
/** Roles where one-street preflop EV is honest (BUILD_PLAN §PF2): opening and defending an open. Every
 *  other role (vs3bet, vs4bet, vsjam) is multi-street-dependent or ICM/push-fold territory and always
 *  abstains to chart-adherence grading. */
const EV_HONEST_ROLES = new Set(['open', 'defend']);
/**
 * Estimate the one-street EV of every honest candidate intent for this preflop decision, and the loss of
 * the CHOSEN intent. Pure function of (ctx, recommendation, chosen, inputs) - abstains (evLossBB: null)
 * for any role outside {open, defend}, per the BUILD_PLAN boundary.
 */
export const estimatePreflopEv = (ctx, recommendation, chosen, inputs = {}) => {
    const role = recommendation.role;
    // PF5 composition: a push/fold-routed recommendation (short-stack / tournament, resolved off the DT
    // Nash charts) is ICM/Nash territory - one street of algebra cannot honestly price a jam or a call-off,
    // and a correct Nash FOLD at an `open` node must NOT be run through open-EV and graded down. So abstain
    // to Nash chart-adherence whenever `pushFold` is set, in ADDITION to the always-abstaining roles. This
    // is exactly the "abstain → grade on chart/rule compliance" valve; the push/fold recommendation carries
    // the Nash finalIntent + alternatives that gradeAbstention scores against.
    if (recommendation.pushFold || !EV_HONEST_ROLES.has(role)) {
        const chosenScore = { intent: chosen, evBB: 0 };
        return {
            perAction: [chosenScore],
            best: chosenScore,
            chosen: chosenScore,
            evLossBB: null,
            abstained: true,
            abstainReason: recommendation.pushFold
                ? 'push/fold-routed (PF5): ICM/Nash short-stack territory, not one-street $EV - graded on Nash chart-adherence'
                : role === 'vs3bet'
                    ? 'vs3bet is multi-street-dependent: one street of algebra cannot honestly rank it, graded on chart-adherence'
                    : 'push-fold/ICM territory (Phase 7 / PF5), not one-street $EV - graded on chart-adherence',
        };
    }
    const equity = inputs.equity ?? 0.5;
    const equityVsContinue = inputs.equityVsContinue ?? equity;
    const foldProb = inputs.foldProb ?? 0.5;
    const deadPot = inputs.deadMoneyBB ?? ctx.potBB ?? 1.5;
    const pocketPair = isPocketPair(ctx.hero.cards);
    const realization = preflopRealizationFactor(ctx.positionClass, pocketPair);
    const openSizeBB = inputs.openSizeBB ?? (recommendation.finalIntent === 'open' ? recommendation.sizing?.toBB : undefined) ?? 2.5;
    const facedBB = ctx.facing.toCallBB ?? 2.5;
    const threeBetSizeBB = inputs.threeBetSizeBB ?? (recommendation.finalIntent === 'three_bet' ? recommendation.sizing?.toBB : undefined) ?? 3 * facedBB;
    let perAction;
    if (role === 'open') {
        perAction = [
            { intent: 'fold', evBB: 0 },
            { intent: 'open', evBB: evRaise(deadPot, openSizeBB, foldProb, equityVsContinue) },
        ];
    }
    else {
        const pot = ctx.potBB ?? deadPot;
        perAction = [
            { intent: 'fold', evBB: 0 },
            { intent: 'call', evBB: evFlat(pot, facedBB, equity, realization) },
            { intent: 'three_bet', evBB: evRaise(pot, threeBetSizeBB, foldProb, equityVsContinue, facedBB) },
        ];
    }
    // the chosen intent fell outside the derived set (e.g. 'limp', or a 'jam' over an open) - this model
    // never priced that action, so abstain rather than fabricate a number for it (same honesty valve as the
    // role gate above). Opus edge-case review (2026-07-14): the prior version guessed a 1bb-call EV here.
    const chosenScore = perAction.find((p) => p.intent === chosen);
    if (!chosenScore) {
        const fallbackBest = [...perAction].sort((a, b) => b.evBB - a.evBB)[0];
        return {
            perAction,
            best: fallbackBest,
            chosen: { intent: chosen, evBB: 0 },
            evLossBB: null,
            abstained: true,
            abstainReason: `'${chosen}' is not a modeled candidate for role '${role}' - one-street EV can't honestly price it, graded on chart-adherence`,
        };
    }
    const sorted = [...perAction].sort((a, b) => b.evBB - a.evBB);
    const best = sorted[0];
    return {
        perAction,
        best,
        chosen: chosenScore,
        evLossBB: Math.max(0, best.evBB - chosenScore.evBB),
        abstained: false,
    };
};
/**
 * Derive the preflop-discipline error codes for a decision (BUILD_PLAN §PF2: emit the already-declared
 * OPENED_OUT_OF_RANGE / CALLED_3BET_OOP_TRASH / BB_OVERFOLD_VS_BTN codes, dimensions.ts:37-39). Pure
 * function of (ctx, recommendation, user) - a deviation detector, not an EV judgement (EV is separate).
 */
export const derivePreflopErrorCodes = (ctx, recommendation, user) => {
    const codes = [];
    const { role, finalIntent } = recommendation;
    // PF5: the three declared codes are cash-chart discipline (opened-out-of-range / OOP-3bet-call-trash /
    // BB-overfold) - none is a push/fold code, and their predicates would misread a Nash jam-or-fold node
    // (e.g. a min-open at 12bb is a leak but not "opened out of range"). A push/fold-routed decision has its
    // own Nash discipline, graded by chart-adherence above; emit no cash codes for it.
    if (recommendation.pushFold)
        return codes;
    // opened a hand the chart (post-rules) does not sanction opening - an out-of-range steal/iso.
    if (role === 'open' && user.intent === 'open' && finalIntent !== 'open') {
        codes.push('OPENED_OUT_OF_RANGE');
    }
    // called a 3bet out of position with a hand below the chart's v3b-call floor - a trash flat OOP.
    if (role === 'vs3bet' && ctx.positionClass === 'OOP' && user.intent === 'call' && finalIntent === 'fold') {
        codes.push('CALLED_3BET_OOP_TRASH');
    }
    // folded the big blind vs a button open when the chart says defend (call or 3bet) - overfolding the
    // single widest, best-priced defend spot at the table.
    if (role === 'defend' &&
        ctx.hero.position === 'BB' &&
        ctx.facing.openerPosition === 'BTN' &&
        user.intent === 'fold' &&
        finalIntent !== 'fold') {
        codes.push('BB_OVERFOLD_VS_BTN');
    }
    return codes;
};
/**
 * Grade one preflop decision. Runs the honest one-street EV estimate where the role supports it (open,
 * defend), or falls back to chart-adherence (gradeAbstention, bands.ts) for every other role, exactly the
 * abstention pattern grading/grade.ts already uses postflop. Attributes error codes to the
 * preflop_discipline dimension via the existing (role-agnostic) dimensions.ts machinery.
 */
export const gradePreflopDecision = (input) => {
    const { ctx, recommendation, user } = input;
    const ev = estimatePreflopEv(ctx, recommendation, user.intent, input.evInputs);
    let graded;
    if (ev.abstained) {
        const matchedFinal = user.intent === recommendation.finalIntent;
        const plausible = matchedFinal || recommendation.alternatives.some((a) => a.intent === user.intent);
        graded = gradeAbstention(matchedFinal, plausible);
    }
    else {
        graded = gradeFromEvLoss(ev.evLossBB ?? 0);
    }
    const errorCodes = derivePreflopErrorCodes(ctx, recommendation, user);
    const skillScores = attributeDimensions(errorCodes, ev.evLossBB, graded.score);
    return {
        grade: graded.score,
        gradeBand: graded.band,
        evLossBB: ev.evLossBB,
        abstained: ev.abstained,
        errorCodes,
        skillScores,
    };
};
export { DEFAULT_NOISE_FLOOR_BB as PREFLOP_DEFAULT_NOISE_FLOOR_BB };
//# sourceMappingURL=preflop.js.map